import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IWatrReimbursement, IWageAndTravelVerifyValidation, IWatrRequest } from '@nkr/models/index';
import { Button, Panel, Alert } from 'react-bootstrap';
import { FileDocumentType } from '@nkr/enums/document-type';
import WatrInfoAccordions from '../accordion';
import styled from 'styled-components';
import Header from '../header';
import VerificationStepTracker from '../verification-step-tracker';
import { DonorShieldContext } from '../context';
import SsnInput from '../common/ssn-input';
import ConnectedDevMockDocsOffer from '../dev-mock-docs/ConnectedDevMockDocsOffer';
import { IWatrViewerDocument } from '../../utils/watr-viewer-document';

const FormWrapper = styled.div`
    .panel-body .control-label {
        color: #475569;
        font-weight: 600;
    }
    .panel-body .form-control {
        border-radius: 6px;
        border-color: #e2e8f0;
    }
    .panel-body .form-control:focus {
        border-color: #94a3b8;
        box-shadow: 0 0 0 2px rgba(148,163,184,0.2);
    }
    .selected-btn {
        border: 1px solid #3b82f6;
        background: #eff6ff;
        color: #1d4ed8;
    }
`;

const CardPanel = styled(Panel)`
    margin-top: 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    overflow: hidden;
`;

const PanelSubtitle = styled.p`
    margin: 0 0 20px 0;
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
    letter-spacing: 0.01em;
`;

const SsnBlock = styled.div<{ $viewing?: boolean }>`
    padding: 14px 16px;
    background: ${p => p.$viewing ? '#eff6ff' : '#f8fafc'};
    border-radius: 8px;
    border: 1px solid ${p => p.$viewing ? '#93c5fd' : '#e2e8f0'};
    box-shadow: ${p => p.$viewing ? 'inset 3px 0 0 #3b82f6' : 'none'};
    margin-bottom: 20px;
`;

const ActionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 18px 20px;
    margin-top: 4px;
    background: #fafbfc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
`;

const ViewActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
`;

const REQUEST_FILE_TYPE_SSN = 8;

const hasSsnVerificationFile = (data: IWatrRequest): boolean =>
    (data.fileUploadRecords || []).some((r) => {
        const type = Number(r.fileType);
        return r.isActive !== false && !Number.isNaN(type) && type === REQUEST_FILE_TYPE_SSN;
    });

interface IProps {
    data: IWatrRequest,
    isAudit: boolean,
    reimbursements: IWatrReimbursement[],
    validation: IWageAndTravelVerifyValidation,
    isDirty: boolean,
}

const VerifyTax = (props: IProps) => {
    const [ssnDecrypted, setSsnDecrypted] = useState('');
    const [viewButton, setViewButton] = useState<number | null>(null);
    const fileLoadAttemptedForRequestId = useRef<number | null>(null);
    const context = useContext(DonorShieldContext);

    const hasSsnDoc = hasSsnVerificationFile(props.data);

    const viewerDocs = useMemo((): IWatrViewerDocument[] => {
        if (!hasSsnDoc) {
            return [];
        }

        return [{
            docType: FileDocumentType.SsnVerification,
            lookupId: props.data.id,
            label: '1 · SS# Verification'
        }];
    }, [props.data.id, hasSsnDoc]);

    const viewingSsn = viewButton === FileDocumentType.SsnVerification;

    const handleViewerDocChange = useCallback((docType: number) => {
        setViewButton(docType);
    }, []);

    const openViewerAtIndex = useCallback(async (index: number) => {
        if (!viewerDocs.length || index < 0 || index >= viewerDocs.length) {
            return;
        }

        const doc = viewerDocs[index];
        const fileUrl = await context.fetchFileUrl(doc.docType, doc.lookupId);

        if (!fileUrl) {
            return;
        }

        setViewButton(doc.docType);
        context.onShowDataSideBar(
            doc.docType,
            doc.lookupId,
            fileUrl,
            viewerDocs.length,
            undefined,
            index,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            viewerDocs,
            handleViewerDocChange
        );
    }, [viewerDocs, context, handleViewerDocChange]);

    const handleViewFile = async (docType: number) => {
        const index = viewerDocs.findIndex(d => d.docType === docType);

        if (index < 0) {
            return;
        }

        await openViewerAtIndex(index);
    };

    useEffect(() => {
        setSsnDecrypted(props.data.ssnDecrypted);

        const requestId = props.data.id;

        if (fileLoadAttemptedForRequestId.current === requestId) {
            return;
        }

        if (!viewerDocs.length) {
            return;
        }

        fileLoadAttemptedForRequestId.current = requestId;
        openViewerAtIndex(0);
    }, [props.data, viewerDocs, openViewerAtIndex]);

    const showDeclineModal = () => {
        context.onShowDeclineModal(props.data.id, false);
    };

    const verifyTax = () => {
        context.onVerifyTax(
            props.data.id,
            ssnDecrypted,
            props.data.incomeIrsYear || 0,
            props.data.incomeIrsYearGross || 0,
            false
        );
    };

    const missingDocs: string[] = [];

    if (!hasSsnDoc) {
        missingDocs.push('SS# verification document');
    }

    return (
        <FormWrapper>
            <ConnectedDevMockDocsOffer
                data={props.data}
                downloadFile={(docType, lookupId) => context ? context.downloadFile(docType, lookupId as number) : Promise.resolve()}
                includeWages={true}
                includeReceipts={false}
                forceWageSlots={{ forceSsn: true }}
                onComplete={() => {
                    fileLoadAttemptedForRequestId.current = null;

                    if (context.onRefreshRequest) {
                        context.onRefreshRequest();
                    }
                }}
            />
            <Header data={props.data} showStatus={false} />
            <VerificationStepTracker data={props.data} />

            <CardPanel>
                <Panel.Body style={{ padding: '20px 24px' }}>
                    <PanelSubtitle>
                        Confirm SSN against the SS# verification document, then Verify or Decline.
                    </PanelSubtitle>

                    <SsnBlock $viewing={viewingSsn}>
                        <SsnInput
                            value={ssnDecrypted}
                            onChange={(value) => setSsnDecrypted(value)}
                            filesTaxesInUs={props.data.filesTaxesInUs}
                            isDirty={props.isDirty}
                            isValid={props.validation.ssnDecrypted}
                        />
                        <ViewActions>
                            <Button
                                className={viewButton === FileDocumentType.SsnVerification ? 'selected-btn' : ''}
                                disabled={!hasSsnDoc}
                                onClick={() => handleViewFile(FileDocumentType.SsnVerification)}
                            >
                                {viewingSsn ? '1 · Viewing SS# Document' : '1 · View SS# Document'}
                            </Button>
                            {!hasSsnDoc && (
                                <Alert bsStyle='warning' style={{ marginBottom: 0, borderRadius: 6, padding: '8px 12px' }}>
                                    SS# verification document not found.
                                </Alert>
                            )}
                        </ViewActions>
                    </SsnBlock>

                    {missingDocs.length > 0 && (
                        <Alert bsStyle='danger' style={{ marginBottom: 0, borderRadius: 6 }}>
                            Cannot verify until the following are available: {missingDocs.join(', ')}.
                        </Alert>
                    )}
                </Panel.Body>
            </CardPanel>

            <ActionsRow>
                <Button bsStyle='primary' className='btn-danger' onClick={showDeclineModal} style={{ width: 110 }}>
                    Decline
                </Button>
                <Button
                    bsStyle='success'
                    onClick={verifyTax}
                    disabled={missingDocs.length > 0}
                    style={{ width: 110 }}
                >
                    Verify
                </Button>
            </ActionsRow>

            <hr />

            <WatrInfoAccordions
                data={props.data}
                isAudit={props.isAudit}
                reimbursements={props.reimbursements}
            />
        </FormWrapper>
    );
}

export default VerifyTax;
